import { Box } from "@mui/material";
import MetricCard from "../../App/Components/MetricCard/MetricCard";
import { TextPerformanceReportState } from "./TextPerformanceReportState";
import { useFlags } from "launchdarkly-react-client-sdk";
import { IFeatureToggles } from "../../App/Typings/FeatureToggles";

export default function TextMetricCards({ id, state }: { id: string; state: TextPerformanceReportState }) {
  const {
    getters: {
      reportData,
      isLoadingPrevReport,
      prevDeliveredCount,
      prevBounceCount,
      prevReplyCount,
      prevStartDate,
      prevEndDate,
      prevUnsubscribeCount,
      deliveredPercentChange,
      bouncePercentChange,
      replyPercentChange,
      unsubscribePercentChange,
    },
  } = state;

  const { enableEmailsDeliveredAnalytics } = useFlags<IFeatureToggles>(); // used for emails + texts

  const textsDelivered =
    (reportData?.dataPoints ?? []).reduce((acc, curr) => acc + curr.deliveredCount, 0) || 0;
  const textBounces = reportData?.dataPoints.reduce((acc, curr) => acc + curr.bounceCount, 0) || 0;
  const textReplies = reportData?.dataPoints.reduce((acc, curr) => acc + curr.replyCount, 0) || 0;
  const textUnsubscribes = reportData?.dataPoints.reduce((acc, curr) => acc + curr.unsubscribeCount, 0) || 0;

  return (
    <Box
      sx={{
        mb: 3,
        display: "flex",
        flexWrap: "wrap",
        gap: { xs: 1, md: 2 },
        justifyContent: "stretch",
        ".MuiCard-root": {
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          flexBasis: { xs: "100%", sm: "calc(50% - 1rem)", md: "calc(25% - 1rem)" },
          p: 1,
        },
        ".MuiCardContent-root": {
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          p: 1,
          "&:last-child": { pb: 1 },
        },
        ".MuiTypography-root": { p: 0, m: 0 },
        ".MuiTypography-h4": { display: "flex", alignItems: "center", justifyContent: "space-between" },
      }}
    >
      {enableEmailsDeliveredAnalytics && (
        <MetricCard
          id={`${id}-texts-delivered`}
          title="Texts Delivered"
          data={textsDelivered}
          prevData={prevDeliveredCount}
          percentChange={deliveredPercentChange}
          prevStartDate={prevStartDate}
          prevEndDate={prevEndDate}
          isLoadingPrevReport={isLoadingPrevReport}
        />
      )}
      <MetricCard
        id={`${id}-text-replies`}
        title="Total Text Replies"
        data={textReplies}
        prevData={prevReplyCount}
        percentChange={replyPercentChange}
        prevStartDate={prevStartDate}
        prevEndDate={prevEndDate}
        isLoadingPrevReport={isLoadingPrevReport}
      />
      <MetricCard
        id={`${id}-text-bounces`}
        title="Text Bounces"
        data={textBounces}
        prevData={prevBounceCount}
        percentChange={bouncePercentChange}
        prevStartDate={prevStartDate}
        prevEndDate={prevEndDate}
        isLoadingPrevReport={isLoadingPrevReport}
      />
      <MetricCard
        id={`${id}-text-unsubscribes`}
        title="Text Unsubscribes"
        data={textUnsubscribes}
        prevData={prevUnsubscribeCount}
        percentChange={unsubscribePercentChange}
        prevStartDate={prevStartDate}
        prevEndDate={prevEndDate}
        isLoadingPrevReport={isLoadingPrevReport}
        reverse
      />
    </Box>
  );
}
